<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;

    /**
     * Component renders an animated side panel, with a slot for content
     * @property {Boolean} [open] - manages open/closed state
     * @property {String} [side] - which side of the screen to open from; can be 'start' or 'end'
     * @property {Boolean} [inverted] - color scheme inversion
     * @property {String} [id] - id for panel wrapper element
     * @fires 'open' when opening
     * @fires 'close' when closing
     */
    import { createEventDispatcher, tick } from 'svelte';
    import { default as IconBarButton, Skins } from '@oat-sa-private/ui-elements/iconBarButton/IconBarButton.svelte';
    import { __, focusTrap } from '@oat-sa-private/ui-core';

    const dispatch = createEventDispatcher();

    export let open = false;
    export let side = 'start';
    export let inverted = true;
    export let id;
    export let role;
    export let ariaLabel;
    export let ariaLabelledBy;

    /**
     * The Panel's root element
     * @property {DOMElement}
     */
    let panelElement;

    /**
     * The Panel's close button
     * @property {DOMElement}
     */
    let panelCloserElement;

    /**
     * The Panel's drawer content element
     * @property {DOMElement}
     */
    let drawerContentElement;

    /**
     * The element which had focus before Panel opening, and will regain focus after closing
     * @property {DOMElement}
     */
    let previouslyFocused;

    /*
     * On component mount the panel should be hidden immediately without any animation which can not be done by `close` class.
     * Once the component has been opened, it should be closed with the animation.
     */
    let hiddenPanelClass = 'close-initial';

    $: if (open) {
        openPanel();
    }

    function openPanel() {
        dispatch('open');
        previouslyFocused = document && document.activeElement;

        tick().then(() => {
            // Focus the closer
            if (panelElement && panelCloserElement) {
                panelCloserElement.children[0].focus();
            }
        });
    }

    function closePanel() {
        open = false;
        hiddenPanelClass = 'close';
        drawerContentElement.scrollTop = 0;

        dispatch('close');

        if (previouslyFocused) {
            previouslyFocused.focus();
        }
    }

    /**
     * Handle keyboard input:
     * - Escape to close
     * @param {KeyEvent} e
     */
    function handleKeydown(e) {
        if (open && e.key === 'Escape') {
            closePanel();
        }
    }
</script>

<style>
    @keyframes closePanel {
        0% {
            opacity: 1;
            height: 100%;
            width: 100%;
            clip: unset;
        }
        99.99% {
            opacity: 0;
            height: 100%;
            width: 100%;
            clip: unset;
            z-index: var(--layer-1);
        }
        100% {
            opacity: 0;
            z-index: unset;
            visibility: hidden;
            @add-mixin visually-hidden;
        }
    }

    @keyframes openPanel {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }

    @add-mixin dir-style .panel, --dir-rtl-multiplier, 1, -1;

    .panel {
        --drawer-width: 37rem;
        --header-height: 7rem;

        background-color: var(--color-overlay);
        position: fixed;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;

        &.close-initial {
            visibility: hidden;
            @add-mixin visually-hidden;
        }

        &.close {
            animation: closePanel 0.2s;
            animation-delay: 0.2s;
            animation-fill-mode: forwards;
            z-index: var(--layer-5);

            & .drawer {
                transition: transform 0.2s ease;

                &.start {
                    transform: translateX(calc(-1 * var(--dir-rtl-multiplier) * var(--drawer-width)));
                }

                &.end {
                    transform: translateX(calc(var(--dir-rtl-multiplier) * var(--drawer-width)));
                }
            }
        }

        &.open {
            animation: openPanel 0.2s;
            animation-fill-mode: forwards;
            z-index: var(--layer-5);

            & .drawer {
                transition: transform 0.2s ease;
                transition-delay: 0.2s;

                &.start,
                &.end {
                    transform: translateX(0);
                }
            }
        }

        &.inverted-color {
            background-color: var(--color-overlay-inverted);
        }

        & .drawer {
            position: absolute;
            top: 0;
            height: 100%;
            width: var(--drawer-width);

            &:not(.inverted) {
                background-color: var(--color-bg-default);
            }

            &.start {
                inset-inline-start: 0;
                transform: translateX(calc(-1 * var(--dir-rtl-multiplier) * var(--drawer-width)));
            }

            &.end {
                inset-inline-end: 0;
                transform: translateX(calc(var(--dir-rtl-multiplier) * var(--drawer-width)));
            }

            & .header {
                height: var(--header-height);
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding-inline-start: 1.5rem;
                padding-inline-end: 2.5rem;
            }
            &.end .header {
                flex-direction: row-reverse;
                padding-inline-start: 2.5rem;
                padding-inline-end: 1.5rem;
            }
            & .drawer-content {
                height: calc(100% - var(--header-height));
                overflow: auto;
            }
        }
    }
</style>

<div
    {id}
    class="panel {open ? 'open' : hiddenPanelClass}"
    class:inverted-color={inverted}
    role={role || void 0}
    aria-label={ariaLabel || void 0}
    aria-labelledby={ariaLabelledBy || void 0}
    use:focusTrap
    on:keydown={handleKeydown}
    on:click|self|stopPropagation={closePanel}
    bind:this={panelElement}>
    <div class="drawer {side}" class:inverted>
        <div class="header">
            <div class="icon-wrapper" bind:this={panelCloserElement}>
                <IconBarButton label={__('Close Panel')} size="base-16" icon="remove-16" skin={inverted ? Skins.INVERTED: Skins.DEFAULT} on:click={closePanel} />
            </div>
            <slot name="header" />
        </div>
        <div class="drawer-content" bind:this={drawerContentElement}>
            <slot />
        </div>
    </div>
</div>
